import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import imputableService, { Imputable, tipoCuentaLabel } from '../../services/imputableService';
import noImputableService, { formatNoImputableLabel, type NoImputable } from '../../services/noImputableService';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 350;

const visibleEnLabel = (v: string | null | undefined): string => {
  const s = v != null ? String(v).trim() : '';
  if (!s) return 'Ninguno';
  if (/^ambos$/i.test(s)) return 'Ambos';
  if (/^ninguno$/i.test(s)) return 'Ninguno';
  if (s === 'IVarios' || /^ivarios$/i.test(s)) return 'Ingresos varios';
  if (s === 'EVarios' || /^evarios$/i.test(s)) return 'Egresos varios';
  return s;
};

const chipSx = { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } };

const esVisibleParaTipo = (item: Imputable, tipo?: 'I' | 'E'): boolean => {
  if (!tipo) return true;
  const v = String(item.visibleEn ?? '').trim();
  if (!v) return true;
  if (tipo === 'I') return v === 'IVarios' || v === 'Ambos';
  return v === 'EVarios' || v === 'Ambos';
};

export interface ImputableInlineSearchFieldProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  tipoMovimientoIE?: 'I' | 'E';
  onSelect: (imputable: Imputable) => void;
  onOpenModal?: () => void;
  error?: boolean;
  disabled?: boolean;
}

const ImputableInlineSearchField: React.FC<ImputableInlineSearchFieldProps> = ({
  label = 'Concepto imputable',
  placeholder = 'Número de cuenta o nombre',
  helperText,
  tipoMovimientoIE,
  onSelect,
  onOpenModal,
  error = false,
  disabled = false,
}) => {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Imputable[]>([]);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const userEditedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [anchorWidth, setAnchorWidth] = useState(0);
  const [rubrosPorNro, setRubrosPorNro] = useState<Map<number, NoImputable>>(new Map());

  useEffect(() => {
    let cancelled = false;
    noImputableService
      .getAllNoImputablesPlanCuentas()
      .then((rows) => {
        if (!cancelled) setRubrosPorNro(new Map(rows.map((r) => [r.nroRubro, r])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const etiquetaRubro = useCallback(
    (nroRubro?: number) => {
      if (!nroRubro) return '—';
      const rubro = rubrosPorNro.get(nroRubro);
      return rubro ? formatNoImputableLabel(rubro) : `Rubro ${nroRubro}`;
    },
    [rubrosPorNro],
  );

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscarPorNroExacto = useCallback(
    async (soloDigitos: string): Promise<Imputable | null> => {
      if (!soloDigitos) return null;
      const nro = Number(soloDigitos);
      if (!Number.isFinite(nro) || nro <= 0) return null;
      try {
        const item = await imputableService.getImputable(nro);
        if (!item) return null;
        const mapped: Imputable = {
          nroCuenta: item.nroCuenta ?? nro,
          nomCuenta: item.nomCuenta || item.nombre,
          nroRubro: item.nroRubro,
          tipoCuentaImputa: item.tipoCuentaImputa || item.tipo,
          descripcioncta: item.descripcioncta ?? item.descripcion,
          visibleEn: item.visibleEn ?? undefined,
          saldo: item.saldo ?? undefined,
        };
        return esVisibleParaTipo(mapped, tipoMovimientoIE) ? mapped : null;
      } catch {
        return null;
      }
    },
    [tipoMovimientoIE],
  );

  const mergeUnicos = (lista: Imputable[], agregar: Imputable[]) => {
    const out = [...lista];
    for (const item of agregar) {
      if (!out.some((x) => x.nroCuenta === item.nroCuenta)) out.push(item);
    }
    return out;
  };

  const buscarImputables = useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) {
        resetBusqueda();
        setLoading(false);
        return;
      }

      const requestId = ++searchRequestIdRef.current;
      setLoading(true);

      try {
        const result = await imputableService.getImputablesSearchPaged({
          searchTerm: trimmed,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
          tipoMovimientoIE,
        });

        if (requestId !== searchRequestIdRef.current) return;

        let items = result.items ?? [];

        const compact = trimmed.replace(/\s+/g, '');
        if (/^\d+$/.test(compact)) {
          const porNro = await buscarPorNroExacto(compact);
          if (requestId !== searchRequestIdRef.current) return;
          if (porNro) {
            items = mergeUnicos([porNro], items);
          }
        }

        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlightedIndex(items.length > 0 ? 0 : -1);
        itemRefs.current = [];
      } catch {
        if (requestId !== searchRequestIdRef.current) return;
        resetBusqueda();
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [buscarPorNroExacto, resetBusqueda, tipoMovimientoIE],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!isFocused || !userEditedRef.current) return;

    debounceRef.current = setTimeout(() => buscarImputables(term), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarImputables, isFocused]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      setAnchorWidth(containerRef.current.offsetWidth);
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = (imputable: Imputable) => {
    userEditedRef.current = false;
    setTerm('');
    onSelect(imputable);
    resetBusqueda();
    setLoading(false);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      userEditedRef.current = false;
      setTerm('');
      setLoading(false);
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const item = suggestions[idx];
      if (item) handleSelect(item);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!open) {
      userEditedRef.current = false;
      setTerm('');
      setLoading(false);
      resetBusqueda();
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        required
        label={label}
        placeholder={placeholder}
        value={term}
        onChange={(e) => {
          userEditedRef.current = true;
          setTerm(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setIsFocused(true);
          if (suggestions.length > 0) setOpen(true);
        }}
        onBlur={handleBlur}
        disabled={disabled}
        error={error}
        helperText={helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading ? <CircularProgress size={20} sx={{ mr: 0.5 }} /> : null}
              {onOpenModal ? (
                <IconButton
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onOpenModal}
                  edge="end"
                  color="primary"
                  title="Buscar en listado completo"
                  disabled={disabled}
                  size="small"
                >
                  <SearchIcon />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={open && suggestions.length > 0}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        style={{ width: anchorWidth > 0 ? anchorWidth : undefined }}
      >
        <Paper
          ref={listRef}
          elevation={6}
          sx={{ maxHeight: 360, overflowY: 'auto', borderRadius: 2, width: '100%' }}
        >
          <List dense disablePadding>
            {suggestions.map((item, idx) => {
              const tipoLabel = tipoCuentaLabel(item.tipoCuentaImputa);
              const rubroLabel = etiquetaRubro(item.nroRubro);
              const visibleLabel = visibleEnLabel(item.visibleEn);
              const descripcion = item.descripcioncta?.trim();

              return (
              <ListItem key={item.nroCuenta} disablePadding divider={idx < suggestions.length - 1}>
                <ListItemButton
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  selected={idx === highlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  sx={{ py: 1, px: 1.5, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <Box display="flex" alignItems="flex-start" gap={1} minWidth={0}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 64, fontFamily: 'monospace', flexShrink: 0, pt: 0.25 }}
                        >
                          #{item.nroCuenta}
                        </Typography>
                        <Box minWidth={0} flex={1}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, whiteSpace: 'normal' }}>
                            {item.nomCuenta?.trim() || '—'}
                          </Typography>
                          {descripcion && descripcion !== item.nomCuenta?.trim() ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, whiteSpace: 'normal' }}>
                              {descripcion}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75, pl: '72px' }}>
                        <Chip label={`Rubro: ${rubroLabel}`} size="small" variant="outlined" sx={chipSx} />
                        <Chip label={tipoLabel} size="small" color="default" sx={chipSx} />
                        <Chip label={`Visible: ${visibleLabel}`} size="small" variant="outlined" sx={chipSx} />
                      </Stack>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
            })}
          </List>
        </Paper>
      </Popper>
    </Box>
  );
};

export default ImputableInlineSearchField;
